import React, { useState, useEffect } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import {
  FileText, Edit2, Save, X, Plus, Trash2, Loader2, DollarSign, Percent
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const DEFAULTS = {
  abn: '',
  ndis_registration_number: '',
  address: '',
  phone: '',
  payment_terms: '',
  contact_email: '',
  email_message: '',
  payment_rounding: 0,
  ndia_provider_number: '',
  cost_calculation_based_on: 'end_time',
  cancelled_by_client_label: false,
  invoice_item_format: '{SupportItemNumber} {SupportItemName} {Hours}hrs @ ${Rate}',
  fixed_recurring_format: '{SupportItemNumber} {SupportItemName} @ ${Rate}',
  default_due_days: 14,
  invoice_approved_times: false,
  mileage_notional_pricing: false,
  show_total_quantity: false,
  invoice_approved_only: false,
};

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none',
        checked ? 'bg-primary-600' : 'bg-slate-200'
      )}
    >
      <span className={clsx(
        'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-4.5' : 'translate-x-0.5'
      )} />
    </button>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3 border-b border-slate-100 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="text-xs leading-relaxed text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, children, onEdit, onSave, onCancel, editing, saving }) {
  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-primary-600" />}
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
        </div>
        {!editing && onEdit && (
          <button onClick={onEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            <Edit2 size={12} /> Edit
          </button>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <X size={12} /> Cancel
            </button>
            <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Invoices settings panel ───────────────────────────────────────────────────
function InvoicesPanel({ settings, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(settings);

  useEffect(() => { setForm(settings); }, [settings]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', { invoice_settings: form });
      toast.success('Invoice settings saved');
      onSaved(form);
      setEditing(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(settings);
    setEditing(false);
  };

  const inp = 'input-field !py-1.5 text-sm';
  const dis = 'text-sm text-slate-700';

  return (
    <Panel title="Invoices" icon={FileText} editing={editing} onEdit={() => setEditing(true)}
      onSave={handleSave} onCancel={handleCancel} saving={saving}>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <div className="space-y-0">
          <Row label="ABN" hint="Used on invoices and PRODA export">
            {editing
              ? <input className={inp} style={{width:180}} value={form.abn} onChange={e => set('abn', e.target.value)} placeholder="12 345 678 901" />
              : <span className={dis}>{form.abn || '—'}</span>}
          </Row>
          <Row label="NDIS Registration Number" hint="Provider registration number for PRODA bulk claims">
            {editing
              ? <input className={inp} style={{width:180}} value={form.ndis_registration_number || ''} onChange={e => set('ndis_registration_number', e.target.value)} placeholder="4050000123" />
              : <span className={dis}>{form.ndis_registration_number || '—'}</span>}
          </Row>
          <Row label="Phone">
            {editing
              ? <input className={inp} style={{width:180}} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+61 2 1234 5678" />
              : <span className={dis}>{form.phone || '—'}</span>}
          </Row>
          <Row label="Contact Email">
            {editing
              ? <input type="email" className={inp} style={{width:200}} value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="billing@company.com" />
              : <span className={dis}>{form.contact_email || '—'}</span>}
          </Row>
          <Row label="NDIA Provider Number">
            {editing
              ? <input className={inp} style={{width:180}} value={form.ndia_provider_number} onChange={e => set('ndia_provider_number', e.target.value)} />
              : <span className={dis}>{form.ndia_provider_number || '—'}</span>}
          </Row>
          <Row label="Default Invoice Due Days">
            {editing
              ? <input type="number" min={0} className={inp} style={{width:80}} value={form.default_due_days} onChange={e => set('default_due_days', parseInt(e.target.value) || 0)} />
              : <span className={dis}>{form.default_due_days} days</span>}
          </Row>
          <Row label="Payment Rounding" hint="Decimal places for invoice amounts">
            {editing
              ? <select className={inp} style={{width:100}} value={form.payment_rounding} onChange={e => set('payment_rounding', parseInt(e.target.value))}>
                  <option value={0}>0 decimal</option>
                  <option value={2}>2 decimal</option>
                </select>
              : <span className={dis}>{form.payment_rounding} decimal</span>}
          </Row>
          <Row label="Cost Calculation Based On">
            {editing
              ? <select className={inp} style={{width:140}} value={form.cost_calculation_based_on} onChange={e => set('cost_calculation_based_on', e.target.value)}>
                  <option value="end_time">End Time</option>
                  <option value="start_time">Start Time</option>
                </select>
              : <span className={dis}>{form.cost_calculation_based_on === 'end_time' ? 'End Time' : 'Start Time'}</span>}
          </Row>
        </div>

        <div className="space-y-0">
          <Row label="Address">
            {editing
              ? <textarea className={clsx(inp, 'resize-none')} rows={2} style={{width:220}} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St, Sydney NSW 2000" />
              : <span className={dis}>{form.address || '—'}</span>}
          </Row>
          <Row label="Payment Terms">
            {editing
              ? <input className={inp} style={{width:220}} value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} placeholder="e.g. Net 14" />
              : <span className={dis}>{form.payment_terms || '—'}</span>}
          </Row>
          <Row label="Email Message">
            {editing
              ? <textarea className={clsx(inp, 'resize-none')} rows={2} style={{width:220}} value={form.email_message} onChange={e => set('email_message', e.target.value)} placeholder="Please find your invoice attached…" />
              : <span className={dis}>{form.email_message || '—'}</span>}
          </Row>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-0">
        <Row label="Invoice Item Default Format" hint="Template for shift line item descriptions">
          {editing
            ? <input className={inp} style={{width:320}} value={form.invoice_item_format} onChange={e => set('invoice_item_format', e.target.value)} />
            : <span className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-1 rounded">{form.invoice_item_format || '—'}</span>}
        </Row>
        <Row label="Fixed Recurring Invoice Item Default Format">
          {editing
            ? <input className={inp} style={{width:320}} value={form.fixed_recurring_format} onChange={e => set('fixed_recurring_format', e.target.value)} />
            : <span className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-1 rounded">{form.fixed_recurring_format || '—'}</span>}
        </Row>

        <Row label="Cancelled by Client Label" hint="Show a special label for client-cancelled shifts on invoices">
          {editing
            ? <Toggle checked={form.cancelled_by_client_label} onChange={v => set('cancelled_by_client_label', v)} />
            : <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', form.cancelled_by_client_label ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                {form.cancelled_by_client_label ? 'On' : 'Off'}
              </span>}
        </Row>
        <Row label="Invoice Based on Approved Shift Times">
          {editing
            ? <Toggle checked={form.invoice_approved_times} onChange={v => set('invoice_approved_times', v)} />
            : <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', form.invoice_approved_times ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                {form.invoice_approved_times ? 'On' : 'Off'}
              </span>}
        </Row>
        <Row label="Invoice Mileage Based on Notional Pricing">
          {editing
            ? <Toggle checked={form.mileage_notional_pricing} onChange={v => set('mileage_notional_pricing', v)} />
            : <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', form.mileage_notional_pricing ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                {form.mileage_notional_pricing ? 'On' : 'Off'}
              </span>}
        </Row>
        <Row label="Show Total Quantity (Hours) on Invoices">
          {editing
            ? <Toggle checked={form.show_total_quantity} onChange={v => set('show_total_quantity', v)} />
            : <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', form.show_total_quantity ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                {form.show_total_quantity ? 'On' : 'Off'}
              </span>}
        </Row>
        <Row label="Invoice Approved Shifts Only">
          {editing
            ? <Toggle checked={form.invoice_approved_only} onChange={v => set('invoice_approved_only', v)} />
            : <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', form.invoice_approved_only ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                {form.invoice_approved_only ? 'On' : 'Off'}
              </span>}
        </Row>
      </div>
    </Panel>
  );
}

// ── Taxes panel ───────────────────────────────────────────────────────────────
function TaxesPanel({ initial }) {
  const [taxes, setTaxes]     = useState(initial);
  const [editing, setEditing] = useState(null); // id of row being edited
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding]   = useState(false);
  const [newForm, setNewForm] = useState({ name: '', rate: '' });
  const [saving, setSaving]   = useState(false);

  useEffect(() => { setTaxes(initial); }, [initial]);

  const startEdit = (tax) => {
    setEditing(tax.id);
    setEditForm({ name: tax.name, rate: tax.rate });
  };

  const cancelEdit = () => { setEditing(null); setEditForm({}); };

  const saveEdit = async (id) => {
    if (!editForm.name || editForm.rate === '' || editForm.rate === undefined) {
      return toast.error('Name and rate required');
    }
    setSaving(true);
    try {
      const r = await api.put(`/settings/tax-rates/${id}`, {
        name: editForm.name,
        rate: parseFloat(editForm.rate),
      });
      setTaxes(t => t.map(x => x.id === id ? r.data : x));
      setEditing(null);
      toast.success('Tax rate updated');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to update tax rate'); }
    finally { setSaving(false); }
  };

  const deleteTax = async (id) => {
    try {
      await api.delete(`/settings/tax-rates/${id}`);
      setTaxes(t => t.filter(x => x.id !== id));
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  const addTax = async () => {
    if (!newForm.name.trim()) return toast.error('Tax name is required');
    if (newForm.rate === '' || newForm.rate === undefined) return toast.error('Rate is required');
    setSaving(true);
    try {
      const r = await api.post('/settings/tax-rates', {
        name: newForm.name.trim(),
        rate: parseFloat(newForm.rate),
      });
      setTaxes(t => [...t, r.data]);
      setNewForm({ name: '', rate: '' });
      setAdding(false);
      toast.success('Tax rate added');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add tax rate'); }
    finally { setSaving(false); }
  };

  return (
    <Panel title="Taxes" icon={Percent}>
      <div className="flex justify-end mb-2">
        <button onClick={() => setAdding(a => !a)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Plus size={12} /> Add Tax Rate
        </button>
      </div>

      {adding && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <input className="input-field flex-1 !py-1.5 text-sm" placeholder="Tax name (e.g. GST)"
            value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} />
          <div className="relative">
            <input type="number" step="0.01" min="0" max="100"
              className="input-field !py-1.5 text-sm !w-24 pr-7" placeholder="Rate"
              value={newForm.rate} onChange={e => setNewForm(f => ({ ...f, rate: e.target.value }))} />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
          </div>
          <button onClick={addTax} disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
          </button>
          <button onClick={() => setAdding(false)} className="p-1.5 text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody>
            {taxes.length === 0 && (
              <tr><td colSpan={3} className="text-center py-8 text-slate-400">No tax rates configured</td></tr>
            )}
            {taxes.map(tax => (
              <tr key={tax.id} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-3">
                  {editing === tax.id
                    ? <input className="input-field !py-1 text-sm !w-32"
                        value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                    : <span className="font-medium text-slate-800">{tax.name}</span>}
                </td>
                <td className="px-4 py-3">
                  {editing === tax.id
                    ? <div className="relative inline-block">
                        <input type="number" step="0.01" min="0" max="100"
                          className="input-field !py-1 text-sm !w-20 pr-6"
                          value={editForm.rate} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                      </div>
                    : <span className="text-slate-600">{parseFloat(tax.rate).toFixed(2)}%</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {editing === tax.id
                    ? <div className="inline-flex items-center gap-1">
                        <button onClick={() => saveEdit(tax.id)} disabled={saving}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary-600 text-white rounded-lg">
                          {saving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Save
                        </button>
                        <button onClick={cancelEdit} className="p-1 text-slate-400 hover:text-slate-600"><X size={13} /></button>
                      </div>
                    : <div className="inline-flex items-center gap-1">
                        <button onClick={() => startEdit(tax)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Edit2 size={11} /> Edit
                        </button>
                        <button onClick={() => deleteTax(tax.id)}
                          className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </Panel>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function InvoiceSettings() {
  const [invoiceSettings, setInvoiceSettings] = useState(DEFAULTS);
  const [taxRates, setTaxRates]               = useState([]);
  const [loading, setLoading]                 = useState(true);

  useEffect(() => {
    api.get('/settings')
      .then(r => {
        const is = r.data?.settings?.invoice_settings;
        if (is && typeof is === 'object') {
          setInvoiceSettings({ ...DEFAULTS, ...is });
        }
        setTaxRates(r.data?.taxRates || []);
      })
      .catch(() => toast.error('Failed to load invoice settings'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={PAGE}>
      <SettingsHeader title="Invoice settings" description="The business details, defaults and tax rates used on your invoices." />

      <div className="max-w-4xl space-y-6">
        <InvoicesPanel settings={invoiceSettings} onSaved={setInvoiceSettings} />
        <TaxesPanel initial={taxRates} />
      </div>
    </div>
  );
}
